// R115 calendar hub — inspector kind 'day': the PORTFOLIO-DAY agenda (the core
// makes the date header clickable → openInspector({ kind: 'day', id: date })).
// The Ops view's per-day totals live here: cleans, inspections, work orders,
// tasks, arrivals / departures, and who's on (each cleaner's load vs their caps,
// from the hk_capacity layer). Every row opens its own entity in the panel (the
// back stack returns here); the blue action is "Plan cleans".
import { registerInspector, registerSection, registerAction, type CalendarCtx, type EntityRef } from '../registry';
import { dayAgenda, inspStateLabel, inspKindLabel, addDays, type CapLayer } from '../../../../../lib/calendarOps.mjs';
import { OFFICE, views, insp, tasks, propName, fetchLayer, oneShot, pill } from './util';
import { openPlanCleans } from './plan';

interface DayData { cap: CapLayer | null; capError: string | null }
const OPS = views('ops', 'ops.hk', 'ops.maint');
const D = (ref: EntityRef) => ref.id;

registerInspector({
  kind: 'day',
  title: (ref, _d, ctx) => ctx.fmt.wmd(D(ref)),
  sub: (ref, _d, ctx) => {
    const a = dayAgenda({ D: D(ref), today: ctx.asOf, data: ctx.data, jobs: null, tasks: null });
    return `The whole portfolio · ${a.totals.arrivals} arriving · ${a.totals.departures} leaving${D(ref) === ctx.asOf ? ' · today' : ''}`;
  },
  load: async (ref, signal): Promise<DayData> => {
    try { return { cap: await fetchLayer<CapLayer>('hk_capacity', D(ref), D(ref), signal), capError: null }; }
    catch (e) { return { cap: null, capError: (e as Error).message || 'unavailable' }; }
  },
});

const agendaOf = (ref: EntityRef, ctx: CalendarCtx) => {
  const I = insp(ctx), T = tasks(ctx);
  return dayAgenda({ D: D(ref), today: ctx.asOf, data: ctx.data, jobs: I ? I.jobs : null, tasks: T ? T.items : null });
};
const row = (ctx: CalendarCtx, attrs: string, main: string, sub: string, right = '') =>
  `<button type="button" class="opv-row" ${attrs}><span class="opv-row-main"><b>${main}</b><small>${sub}</small></span>${right}</button>`;
const empty = (t: string) => `<div class="opv-muted opv-empty">${t}</div>`;

registerSection({
  inspector: 'day', key: 'day_totals', title: 'Totals', order: 5, openIn: views('stays', 'ops', 'ops.hk', 'ops.maint', 'guests', 'mtr', 'health'),
  when: () => true,
  render: (ref, _d, ctx) => {
    const t = agendaOf(ref, ctx).totals;
    const cell = (icon: string, n: number | null, label: string, sub = '', tone = '') => `<div class="opv-tot${tone ? ` opv-tot-${tone}` : ''}"><span class="opv-tot-n">${icon} ${n == null ? '—' : n}</span><span class="opv-tot-l">${label}</span>${sub ? `<span class="opv-tot-s">${sub}</span>` : ''}</div>`;
    return `<div class="opv-tots">`
      + cell('🧹', t.cleans, 'Cleans', `${t.cleans_done} done · ${t.cleans_unassigned} unassigned`, t.cleans_unassigned || t.at_risk ? 'bad' : '')
      + cell('🔍', t.inspections, 'Inspections', t.inspections_unassigned == null ? 'unavailable' : `${t.inspections_unassigned} unassigned`, t.inspections_unassigned ? 'warn' : '')
      + cell('🔧', t.wos, 'Work orders', 'booked')
      + cell('✓', t.tasks, 'Tasks', D(ref) === ctx.asOf ? 'due + overdue' : 'due')
      + cell('🛬', t.arrivals, 'Arriving')
      + cell('🛫', t.departures, 'Leaving')
      + `</div>`;
  },
});
registerSection({
  inspector: 'day', key: 'day_cleans', title: 'Cleans', order: 10, openIn: OPS,
  when: () => true,
  render: (ref, _d, ctx) => {
    const list = agendaOf(ref, ctx).turns;
    if (!list.length) return empty('No cleans this day.');
    return list.map((t: any) => row(ctx, `data-opv-open="turn" data-id="${ctx.esc(t.id)}" data-lid="${ctx.esc(t.listing_id)}"`,
      ctx.esc(propName(ctx, t.listing_id)),
      `${t.done ? 'Done' : ctx.esc(String(t.status || '').replace('_', ' '))}${t.same_day ? ' · same-day' : ''}${t.moved_from ? ` · ↪ from ${ctx.esc(ctx.fmt.md(t.moved_from))}` : ''} · ${t.assignees && t.assignees.length ? ctx.esc(t.assignees.join(', ')) : 'no cleaner'}`,
      t.unassigned && !t.done ? pill(ctx, 'Assign', 'warn') : t.at_risk ? pill(ctx, 'At risk', 'bad') : '')).join('');
  },
  bind: (root, ref, _d, ctx) => bindRows(root, ref, ctx),
});
registerSection({
  inspector: 'day', key: 'day_inspections', title: 'Inspections', order: 20, openIn: OPS,
  when: () => true,
  render: (ref, _d, ctx) => {
    const list = agendaOf(ref, ctx).inspections;
    if (list == null) return empty('Inspections could not be read — numbers hidden, not zero.');
    if (!list.length) return empty('No inspections this day.');
    return list.map(j => row(ctx, `data-opv-open="inspection" data-id="${ctx.esc(j.id)}" data-lid="${ctx.esc(j.listing_id)}" data-date="${ctx.esc(j.date)}"`,
      ctx.esc(propName(ctx, j.listing_id)), `${ctx.esc(inspKindLabel(j))} · ${ctx.esc(j.inspector || 'no inspector')}${j.state !== 'assigned' && j.state !== 'unassigned' && !j.missed ? ` · ${ctx.esc(inspStateLabel(j))}` : ''}`,
      j.missed ? pill(ctx, '⏰ Missed', 'bad') : j.state === 'unassigned' ? pill(ctx, 'Assign', 'warn') : '')).join('');
  },
  bind: (root, ref, _d, ctx) => bindRows(root, ref, ctx),
});
registerSection({
  inspector: 'day', key: 'day_wos', title: 'Work orders', order: 30, openIn: views('ops', 'ops.maint'),
  when: () => true,
  render: (ref, _d, ctx) => {
    const list = agendaOf(ref, ctx).wos;
    if (!list.length) return empty('No work orders booked this day.');
    return list.map((w: any) => row(ctx, `data-opv-open="wo" data-id="${ctx.esc(w.id)}" data-lid="${ctx.esc(w.listing_id)}"`,
      `${ctx.esc(propName(ctx, w.listing_id))}`, `${ctx.esc(w.title)}${w.who ? ` · ${ctx.esc(w.who)}` : ' · nobody booked'}${w.due_only ? ' · due (not booked)' : ''}`,
      w.priority === 'emergency' || w.priority === 'urgent' ? pill(ctx, w.priority, 'bad') : '')).join('');
  },
  bind: (root, ref, _d, ctx) => bindRows(root, ref, ctx),
});
registerSection({
  inspector: 'day', key: 'day_tasks', title: 'Tasks', order: 40, openIn: views('ops'),
  when: () => true,
  render: (ref, _d, ctx) => {
    const list = agendaOf(ref, ctx).tasks;
    if (list == null) return empty('Tasks could not be read.');
    if (!list.length) return empty('No tasks due this day.');
    return list.map(t => row(ctx, `data-opv-open="task" data-id="${ctx.esc(t.id)}"`,
      ctx.esc(t.title), `${ctx.esc(propName(ctx, t.listing_id))}${t.assignees.length ? ` · ${ctx.esc(t.assignees.map(a => a.name).join(', '))}` : ''}`,
      t.overdue ? pill(ctx, 'Overdue', 'bad') : '')).join('');
  },
  bind: (root, ref, _d, ctx) => bindRows(root, ref, ctx),
});
registerSection({
  inspector: 'day', key: 'day_crew', title: "Who's on", order: 50, openIn: views('ops.hk'),
  when: () => true,
  render: (ref, data, ctx) => {
    const d = data as DayData | null;
    if (!d || !d.cap) return empty(`Cleaner load unavailable${d && d.capError ? ` (${ctx.esc(d.capError)})` : ''}.`);
    const day = d.cap.days[D(ref)];
    if (!day || !day.booked.length) return empty('No cleaner is booked this day yet.');
    return day.booked.map(b => `<div class="opv-row opv-row-static"><span class="opv-row-main"><b>${ctx.esc(b.name)}</b><small>${b.cleans}/${b.cap} cleans${b.same_day ? ` · ${b.same_day}${b.same_day_cap != null ? `/${b.same_day_cap}` : ''} same-day` : ''}${b.off ? ' · scheduled OFF' : ''}</small></span>${b.over ? pill(ctx, 'Over capacity', 'bad') : pill(ctx, 'OK', 'ok')}</div>`).join('')
      + `<p class="opv-muted">Caps come from Housekeeping → schedules (default ${d.cap.caps.max_cleans_default} cleans/day${d.cap.caps.max_same_day_default != null ? `, ${d.cap.caps.max_same_day_default} same-day` : ''}); vendors count too.</p>`;
  },
});
function bindRows(root: HTMLElement, ref: EntityRef, ctx: CalendarCtx): void {
  root.querySelectorAll<HTMLElement>('[data-opv-open]').forEach(b => b.addEventListener('click', () => {
    const kind = b.dataset.opvOpen || '';
    ctx.openInspector({ kind, id: b.dataset.id || '', listing_id: b.dataset.lid || undefined, date: b.dataset.date || D(ref) });
  }));
}

registerAction({
  key: 'plan_cleans', inspector: 'day', label: 'Plan cleans', icon: '🗓', order: 10, roles: OFFICE, primaryIn: views('ops', 'ops.hk'),
  when: (ref, _d, ctx) => D(ref) >= ctx.asOf && D(ref) <= addDays(ctx.asOf, 6),
  run: (_ref, _d, ctx) => openPlanCleans(ctx),
});
registerAction({
  key: 'day_opsboard', inspector: 'day', label: 'Open the Ops board', icon: '↗', order: 20, roles: OFFICE,
  when: () => true,
  run: (_ref, _d, ctx) => ctx.go('hk-insp', 'ops-board'),
});
registerAction({
  key: 'day_inspections', inspector: 'day', label: 'Open Inspections for this day', icon: '🔍', order: 30, roles: OFFICE,
  when: (ref, _d, ctx) => D(ref) >= ctx.asOf,
  run: (ref, _d, ctx) => { oneShot('insp_queue_date', D(ref)); ctx.go('hk-insp', 'inspections'); },
});
// the same planner from a clean's panel (the HK hub's smart scheduler, one tap away)
registerAction({
  key: 'plan_cleans', inspector: 'turn', label: 'Plan the week’s cleans…', icon: '🗓', order: 90, roles: OFFICE,
  when: () => true,
  run: (_ref, _d, ctx) => openPlanCleans(ctx),
});
